/**
 * /app/$tenantSlug/attendance/import/runs — biometric import history
 * (migration 083). One row per committed file import OR device push batch
 * (GET /api/v1/attendance/import/runs). HR-admin, time_tracking-gated.
 */

import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Upload } from "lucide-react";
import { TimeAndLeaveShell } from "@/components/shared/time-and-leave-shell";
import { useHrRoles } from "@/components/hr/use-hr-roles";
import { useImportRuns } from "@/hooks/use-attendance-import";
import { useTenantSlug } from "@/lib/use-tenant-slug";
import { toApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/empty-state";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/attendance/import/runs",
)({
  component: ImportRunsRoute,
});

function ImportRunsRoute() {
  const tenantSlug = useTenantSlug();
  const { isHrAdmin } = useHrRoles();
  const q = useImportRuns();
  const runs = q.data?.runs ?? [];

  return (
    <TimeAndLeaveShell
      title="Import History"
      description="Every committed biometric file import and device push batch."
      actions={
        <Button asChild className="gap-1.5">
          <Link to="/app/$tenantSlug/attendance/import" params={{ tenantSlug }}>
            <Upload className="h-4 w-4" />
            New Import
          </Link>
        </Button>
      }
    >
      {!isHrAdmin ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            Only company admins and HR managers can view import history.{" "}
            <Link
              to="/app/$tenantSlug/my-time"
              params={{ tenantSlug }}
              className="font-medium text-primary underline-offset-4 hover:underline"
            >
              Back to My Time
            </Link>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="pt-6">
            {q.isLoading && (
              <div className="space-y-2">
                {["r1", "r2", "r3"].map((k) => (
                  <Skeleton key={k} className="h-12 w-full" />
                ))}
              </div>
            )}
            {q.isError && (
              <Alert variant="destructive">
                <AlertTitle>Could not load import history</AlertTitle>
                <AlertDescription>{toApiError(q.error).message}</AlertDescription>
              </Alert>
            )}
            {!q.isLoading && !q.isError && runs.length === 0 && (
              <EmptyState
                title="No Imports Yet"
                description="Upload your first biometric machine export to get started."
                action={
                  <Button variant="outline" asChild className="gap-1.5">
                    <Link
                      to="/app/$tenantSlug/attendance/import"
                      params={{ tenantSlug }}
                    >
                      <ArrowLeft className="h-4 w-4" />
                      Import a File
                    </Link>
                  </Button>
                }
              />
            )}
            {!q.isLoading && !q.isError && runs.length > 0 && (
              <div className="overflow-hidden rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>File</TableHead>
                      <TableHead className="w-24">Source</TableHead>
                      <TableHead className="w-20 text-right">Parsed</TableHead>
                      <TableHead className="w-20 text-right">Created</TableHead>
                      <TableHead className="w-24 text-right">Duplicates</TableHead>
                      <TableHead className="w-20 text-right">Errors</TableHead>
                      <TableHead className="w-44">When</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {runs.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell className="max-w-[16rem] truncate text-sm font-medium">
                          {r.file_name}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-xs">
                            {r.source === "push" ? "device push" : r.source}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right text-sm">
                          {r.rows_parsed}
                        </TableCell>
                        <TableCell className="text-right text-sm font-medium text-green-700">
                          {r.punches_created}
                        </TableCell>
                        <TableCell className="text-right text-sm text-muted-foreground">
                          {r.duplicates}
                        </TableCell>
                        <TableCell className="text-right text-sm">
                          {r.errors > 0 ? (
                            <span className="font-medium text-red-700">
                              {r.errors}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">0</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDateTime(r.created_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </TimeAndLeaveShell>
  );
}
